/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Vishal Goel' <vishal@wakencode.com>, February 2020
 *
 *****************************************************************************/

import sortBy from "lodash/sortBy";
import filter from "lodash/filter";
import differenceBy from "lodash/differenceBy";
import cloneDeep from "lodash/cloneDeep";
import intersectionBy from "lodash/intersectionBy";
import uuid from "uniqid";
import { getUpdatedTimeStatus, isBeforeTenMinutes } from "./utlilty";
import { toast } from "react-toastify";
import countBy from "lodash/countBy";
import { constant } from "./constants/constants";

// process vehicles data came from api
export const processVehiclesData = (data) => {
  // filter users
  let users = filter(data, (o) => o.deviceType) || [];

  // filter vehicles
  let allVehicles = filter(data, (o) => !o.deviceType) || [];

  // filter paired vehicles from all vehicles
  let pairedVehicles = filter(allVehicles, (o) => o.userName) || [];

  // filter unpaired vehicles from all vehicles
  let unPairedVehicles = filter(allVehicles, (o) => !o.userName) || [];

  // filter paired users
  let pairedUsers = intersectionBy(users, pairedVehicles, "userName") || [];

  // filter unpaired users
  let unPairedUsers = differenceBy(users, pairedVehicles, "userName") || [];

  pairedUsers = pairedUsers.map((o) => {
    return {
      userName: o.userName,
      id: o.userId.toString(),
    };
  });

  unPairedUsers.map((o) => {
    return (
      (o.key = uuid()),
      (o.status = constant.ONLINE),
      (o.color = "green"),
      (o.type = "unPairedUser"),
      (o.id = o.userId.toString())
    );
  });

  pairedVehicles.map((o) => {
    return (
      (o.key = uuid()),
      (o.status = constant.ONLINE),
      (o.color = "green"),
      (o.type = "paired"),
      (o.id = o.vehicleId.toString())
    );
  });

  unPairedVehicles.map((o) => {
    return (
      (o.key = uuid()),
      (o.status = constant.OFFLINE),
      (o.color = "gray"),
      (o.type = "unPaired"),
      (o.id = o.vehicleId.toString())
    );
  });

  unPairedUsers = sortBy(unPairedUsers, (o) => o.userName);
  pairedVehicles = sortBy(pairedVehicles, (o) => o.vehicleName);
  unPairedVehicles = sortBy(unPairedVehicles, (o) => o.vehicleName);

  let processedData = [
    ...pairedVehicles,
    ...unPairedVehicles,
    ...unPairedUsers,
  ];

  return { vehicles: processedData, pairedUsers: pairedUsers };
};

// process data for live vehicle moving initial state
export const makeEmptyCoordinatesArray = (vehicles) => {
  return filter(vehicles, (o) => o.id).map((v) => {
    let obj = {
      key: v.key,
      equipmentId: v.id,
      userId: v.userId,
      vehicleId: v.vehicleId,
      userName: v.userName,
      pos: [],
      status: v.status,
      isLastCoordinatePresent: false,
      coordinatesType: "vehicle",

      lastUpdatedStatus: v.lastCoordinate
        ? getUpdatedTimeStatus(v.lastCoordinate.timestamp)
        : constant.UNKNOWN,

      lastUpdatedTimestamp: v.lastCoordinate
        ? v.lastCoordinate.timestamp
        : null,
    };
    if (
      v.lastCoordinate &&
      v.lastCoordinate.latitude &&
      v.lastCoordinate.longitude
    ) {
      obj.isLastCoordinatePresent = true;
      obj.pos.push({
        lat: v.lastCoordinate.latitude,
        lng: v.lastCoordinate.longitude,
      });
    }
    return obj;
  });
};

export const filterAndUpdateCoordinates = (
  pairedUsers,
  liveCoordinates,
  vehiclesCoordinates
) => {
  // clone last updated vehicles
  let updatedVehiclesCoordinates = cloneDeep(vehiclesCoordinates);

  // ids of vehicles which are not offline
  let ids = updatedVehiclesCoordinates
    .filter((v) => v.equipmentId && v.status !== constant.OFFLINE)
    .map((v) => v.equipmentId);

  let pairedUserIds = pairedUsers.map((o) => o.id);

  let allIds = [...ids, ...pairedUserIds];

  let coordinates = filter(liveCoordinates, (o) =>
    allIds.includes(o.equipmentId.toString())
  );

  for (let v of updatedVehiclesCoordinates) {
    for (let c of coordinates) {
      // if device id matches with incoming live coordinates device id
      // add live coordinate
      if (v.equipmentId === c.equipmentId.toString()) {
        // if default location is present in pos array clear it and add new coordinates
        if (v.isLastCoordinatePresent) {
          v.pos = [];
          v.isLastCoordinatePresent = false;
        }
        v.pos.push({
          lat: c.latitude,
          lng: c.longitude,
        });
        v.lastUpdatedTimestamp = c.timestamp;

        // tells by which medium last coordinate updated
        v.equipmentType = "vehicle";
      }

      // if coordinates are not coming from vehicle since 10 minutes
      // or last updated coordinate came from mobile, use mobile coordinates or
      if (
        isBeforeTenMinutes(v.lastUpdatedTimestamp) ||
        v.equipmentType === "MOBILE"
      ) {
        if (v.userId === c.equipmentId.toString()) {
          if (v.isLastCoordinatePresent) {
            v.pos = [];
            v.isLastCoordinatePresent = false;
          }
          v.pos.push({
            lat: c.latitude,
            lng: c.longitude,
          });

          // tells by which medium last coordinate updated
          v.coordinatesType = "user";
          v.lastUpdatedTimestamp = c.Timestamp;
        }
      }
    }
    // last updated status in seconds,minutes....
    v.lastUpdatedStatus = getUpdatedTimeStatus(v.lastUpdatedTimestamp);
  }

  return updatedVehiclesCoordinates;
};

// show toast about vehicle paired/unpaired info
export const showVehicleInfoToast = (info) => {
  let totalUnpairedUsers = countBy(
    info.vehicles,
    (o) => o.type === "unPairedUser"
  ).true;
  let totalVehicles = info.vehicles.length - totalUnpairedUsers;

  toast.info(`${constant.TOTAL_VEHICLES} : ${totalVehicles}`, {
    autoClose: 8000,
  });
  toast.info(`${constant.TOTAL_UNPAIRED_USERS} : ${totalUnpairedUsers}`, {
    autoClose: 8000,
  });
  toast.info(`${constant.TOTAL_PAIRED_USERS} : ${info.pairedUsers.length}`, {
    autoClose: 8000,
  });
};

// return true if given coordinates are current viewport else false
export const isContainsCoordinatesInViewPort = (coords) => {
  if (coords.length < 1) return false;
  for (let c of coords) {
    if (!global.mapRef.getBounds().contains(c)) return false;
  }
  return true;
};
